import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DynamicMaterialsBuilder — replaces the legacy MFC-01…MFC-10 color
 * palette presets. Every variation is now driven by line-item
 * procurement inputs across 7 categories: Paint · Cabinets ·
 * Countertops · Flooring · Backsplash · Hardware.
 *
 * Data flow:
 *   - Reads from /api/design-studio/materials?address=… on mount
 *   - Auto-saves (debounced 700ms) on any edit
 *   - Rolls up to procurement subtotal in real time
 *   - Feeds the Sequential Cascade Engine (STAGE_3_FINANCIAL_COMPILATION)
 */

const CATEGORIES = [
  { key: "paint",           label: "Paint",              icon: "🎨", hint: "Wall + trim finishes" },
  { key: "cabinets",        label: "Cabinets",           icon: "🗄", hint: "Kitchen + bath cabinetry" },
  { key: "countertops",     label: "Countertops",        icon: "🪨", hint: "Quartz · granite · laminate" },
  { key: "flooring",        label: "Flooring",           icon: "🪵", hint: "LVP · tile · carpet" },
  { key: "backsplash",      label: "Backsplash",         icon: "🧱", hint: "Tile · panel · quartz slab" },
  { key: "hardware",        label: "Hardware",           icon: "🔩", hint: "Pulls · faucets · fixtures" },
  { key: "ceiling",         label: "Ceiling Scope",      icon: "🎯", hint: "Flat white · scrape+2-coat · Level-4 skim" },
  { key: "vagrant_defense", label: "Vagrant-Kick Defense",icon: "🔒", hint: "MF-EXT-CLAMP-01 · heavy-duty jamb hardware" },
];

// ─── Granular per-item micro-materials (hands-free) ─────────────
// Renders the exact panel-boxes, gallon totals, tub-clip sets, corner
// locks, and threshold transitions computed by the backend engine
// from the property's dimensions. Every line ships with the formula
// so the owner can audit the math. Registry items 04, 43–47, 50.
const GranularMicroMaterials = ({ unitType, measuredSpecs }) => {
  // Default property spec — expanded to include ceiling height, doors,
  // and windows so the micro-engine has enough to compute per-item
  // hardware counts.
  const defaultSpecs = (u) => {
    const bedrooms  = u === "3bed" ? 3 : u === "1bed" ? 1 : u === "studio" ? 0 : 2;
    const bathrooms = u === "3bed" ? 2 : u === "studio" ? 1 : 2;
    return {
      bedrooms, bathrooms,
      kitchen_sqft: 120, living_sqft: 350, bedroom_sqft: 150, bathroom_sqft: 60,
      ceiling_height: 9,
      exterior_doors: 1, interior_doors: 4 + bedrooms, windows: 4 + bedrooms * 2,
      waste_factor: 0.10,
    };
  };
  const [engine, setEngine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/design/micro-materials`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(measuredSpecs || defaultSpecs(unitType)),
        });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setEngine(d);
        }
      } catch { /* silent */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [unitType, measuredSpecs]);

  return (
    <div data-testid="granular-micro-materials" style={{
      marginTop: 14, padding: "14px 14px 16px",
      background: "rgba(0,255,255,0.04)",
      border: "1px solid rgba(0,255,255,0.25)",
      borderRadius: 12,
    }}>
      <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800, marginBottom: 6 }}>
        &gt; GRANULAR MATERIAL MICRO-ENGINE · PER-ITEM QUANTITIES
      </div>
      <div style={{ color: "#B4BEC8", fontSize: "0.72rem", lineHeight: 1.55, marginBottom: 10 }}>
        Panel boxes, gallon volumes, tub-clip sets, corner locks, and threshold transitions — computed from {measuredSpecs ? "the walkthrough's measured rooms" : "a standard unit until the walkthrough is measured"}. Every line ships with the formula so the owner can audit the math.
      </div>
      {loading && <div style={{ color: "#7E92A8", fontSize: "0.82rem" }}>Streaming micro-quantities…</div>}
      {engine && (
        <>
          <div style={{ color: "#7E92A8", fontSize: "0.68rem", marginBottom: 8 }}>
            {engine.computed.total_floor_sqft} sqft floor · {engine.computed.wall_sqft} sqft wall · {engine.computed.trim_linear_ft} lf trim · waste {engine.computed.waste_factor_pct}%
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {engine.lines.map(l => (
              <div key={l.id} data-testid={`gmm-${l.id}`} style={{
                display: "flex", alignItems: "center",
                padding: "8px 10px",
                background: "rgba(0,0,0,0.28)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 6,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 700 }}>{l.label}</div>
                  <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", marginTop: 2 }}>
                    {l.formula}
                  </div>
                </div>
                <div style={{ textAlign: "right", flex: "0 0 auto", marginLeft: 10 }}>
                  <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.95rem", fontWeight: 800 }}>
                    {l.qty}
                  </div>
                  <div style={{ color: "#7E92A8", fontSize: "0.62rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    {l.unit}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// MF-EXT-CLAMP-01 — Vagrant-Kick Defense doctrine.
// Now integrated as `vagrant_defense` in the streamed variation
// categories (server.py _VARIATION_STREAMS). This constant is
// retained only for legacy display fallback if a caller renders
// the row without a selected variation.
const blankRow = () => ({
  material_name: "",
  brand:         "",
  sku:           "",
  vendor:        "",
  unit_cost:     "",
  quantity:      1,
});

const WalkthroughLock = ({ w }) => (
  <div data-testid="dmb-walkthrough-lock" style={{ padding: "10px 12px", marginBottom: 12, borderRadius: 10, border: "1px solid rgba(0,220,120,0.45)", background: "rgba(0,220,120,0.08)", color: "#D8F5E6", fontSize: "0.8rem", lineHeight: 1.5 }}>
    <b style={{ color: "#00DC78" }}>{w.tier_label} → {w.variation_id} locked from walkthrough</b> · {w.status === "completed" ? "completed" : "in progress"} · {w.rooms_measured} rooms measured
    {(w.holds?.nspire || w.holds?.clear_unit || w.holds?.structural) && <div style={{ color: "#FFB347" }}>Hold active: {[w.holds.nspire && "NSPIRE life-safety", w.holds.clear_unit && "Clear Unit", w.holds.structural && "Structural / CEO call"].filter(Boolean).join(" · ")}</div>}
    <a href={`/inspect/${w.inspection_id}`} data-testid="dmb-walkthrough-open" style={{ color: "#5AC8D8", display: "inline-block", marginTop: 4 }}>Open walkthrough →</a>
  </div>
);

const LaborAllocations = ({ w }) => (
  <div data-testid="dmb-labor-allocations" style={{ marginTop: 14, padding: "14px", borderRadius: 12, border: "1px solid rgba(255,179,71,0.3)", background: "rgba(255,179,71,0.05)" }}>
    <div style={{ color: "#FFB347", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800, marginBottom: 8 }}>&gt; LABOR ALLOCATIONS · FROM MEASUREMENTS</div>
    {w.labor.map((l) => (
      <div key={l.key} data-testid={`dmb-labor-${l.key}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: "0.8rem", color: "#fff" }}>
        <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700 }}>{l.label}</div><div style={{ color: "#7E92A8", fontSize: "0.66rem", fontFamily: "'JetBrains Mono', monospace" }}>{l.formula}</div></div>
        <div style={{ color: "#FFB347", fontWeight: 800, whiteSpace: "nowrap" }}>${l.total_usd.toLocaleString()}</div>
      </div>
    ))}
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, color: "#fff", fontWeight: 800 }} data-testid="dmb-labor-total"><span>Labor total</span><span>${w.labor_total_usd.toLocaleString()}</span></div>
    {w.auto_spec_total_usd > 0 && <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 4 }}>+ walkthrough hardware spec ${w.auto_spec_total_usd.toLocaleString()}</div>}
  </div>
);

const DynamicMaterialsBuilder = ({ propertyAddress, entityId, marketRent, unitType }) => {
  const [rows, setRows] = useState(() => {
    const init = {};
    CATEGORIES.forEach(c => { init[c.key] = blankRow(); });
    return init;
  });
  const [saved, setSaved] = useState(null);
  const [saveWarning, setSaveWarning] = useState("");
  const [loading, setLoading] = useState(false);

  // ─── Hands-Free Auto-Variation Stream (Feb 12, 2026) ──────────────
  // Per Doctrine v3.0 the platform NEVER forces the owner to type
  // materials. This fetches 3 institutionally-assembled variations
  // from the background data stream endpoint. Owner selects a card;
  // the rows populate automatically and save.
  const [streamLoading, setStreamLoading] = useState(true);
  const [autoVariations, setAutoVariations] = useState([]);
  const [selectedVariationId, setSelectedVariationId] = useState("");
  const [streamPulledAt, setStreamPulledAt] = useState("");
  const [walkthrough, setWalkthrough] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStreamLoading(true);
      try {
        const q = new URLSearchParams({
          unit_type: unitType || "2bed",
          address:   (propertyAddress || "").trim(),
        }).toString();
        const r = await fetch(`${BACKEND_URL}/api/design/auto-variations?${q}`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) {
            setAutoVariations(d.variations || []);
            setStreamPulledAt(d.pulled_at || "");
            setWalkthrough(d.walkthrough || null);
            const locked = d.walkthrough && (d.variations || []).find((v) => v.variation_id === d.walkthrough.variation_id);
            if (locked) selectVariation(locked, `from walkthrough · ${d.walkthrough.tier_label}`);
          }
        }
      } catch { /* silent */ }
      if (!cancelled) setStreamLoading(false);
    })();
    return () => { cancelled = true; };
  }, [unitType, propertyAddress]);

  // Property intelligence panel — hands-free compliance clock, bond
  // schedule, and housing program status pulled from the same
  // background stream.
  const [propIntel, setPropIntel] = useState(null);
  useEffect(() => {
    if (!propertyAddress || !propertyAddress.trim()) { setPropIntel(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const q = encodeURIComponent(propertyAddress.trim());
        const r = await fetch(`${BACKEND_URL}/api/property/intel?address=${q}`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setPropIntel(d);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [propertyAddress]);

  const selectVariation = (v, source) => {
    setSelectedVariationId(v.variation_id);
    const populated = {};
    CATEGORIES.forEach(c => {
      const meta = v.categories?.[c.key] || {};
      populated[c.key] = {
        material_name: meta.spec || "",
        brand:         v.stream_label || "",
        sku:           `${v.variation_id}-${c.key.toUpperCase()}`,
        vendor:        "Auto-stream · MF Digital Platform",
        unit_cost:     meta.unit_price || 0,
        quantity:      meta.quantity   || 1,
      };
    });
    setRows(populated);
    // ON_SCOPE_LOCK_EVENT — emit dimensional + material + variation
    // data downstream instantly so the micro-engine, procurement
    // tally, and visual rendering engine react without any tab
    // navigation. Per master pipeline directive Feb 12, 2026.
    try {
      window.dispatchEvent(new CustomEvent("mf:scope-lock", {
        detail: {
          variation_id:  v.variation_id,
          stream_label:  v.stream_label,
          line_total:    v.line_total,
          categories:    v.categories,
          acclamation:   v.acclamation_clause,
          antimicrobial: v.antimicrobial,
          openings:      v.architectural_openings,
          pulled_at:     new Date().toISOString(),
        },
      }));
    } catch { /* ignore in non-browser env */ }
    toast.success(`Variation ${v.variation_id} locked ${source || ""} — ${v.stream_label}`);
  };

  // Load existing materials for this address on mount / address change
  useEffect(() => {
    if (!propertyAddress || !propertyAddress.trim()) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const q = encodeURIComponent(propertyAddress.trim());
        const r = await fetch(`${BACKEND_URL}/api/design-studio/materials?address=${q}`);
        if (r.ok) {
          const d = await r.json();
          if (!cancelled && d.materials) {
            const merged = {};
            CATEGORIES.forEach(c => {
              merged[c.key] = { ...blankRow(), ...(d.materials[c.key] || {}) };
            });
            setRows(merged);
            setSaved(d.saved_at || null);
          }
        }
      } catch { /* silent */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [propertyAddress]);

  const subtotal = useMemo(() => {
    // Sum every populated row. `vagrant_defense` (MF-EXT-CLAMP-01) now
    // streams as an integrated category from the backend variation, so
    // no separate $89 add-on — the doctrine line rides inside `rows`.
    return CATEGORIES.reduce((sum, c) => {
      const r = rows[c.key] || {};
      const cost = parseFloat(r.unit_cost) || 0;
      const qty = parseFloat(r.quantity) || 0;
      return sum + cost * qty;
    }, 0);
  }, [rows]);

  const linesFilled = useMemo(() =>
    CATEGORIES.filter(c => (rows[c.key]?.material_name || "").trim()).length,
    [rows]);

  // Debounced auto-save
  useEffect(() => {
    if (!propertyAddress || !propertyAddress.trim()) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/design-studio/materials`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            property_address: propertyAddress.trim(),
            entity_id: entityId || null,
            // Materials snapshot streams from the selected variation —
            // `vagrant_defense` (MF-EXT-CLAMP-01) is one of the integrated
            // categories so no separate `security_clamp` merge is needed.
            materials: rows,
            procurement_subtotal: Math.round(subtotal * 100) / 100,
          }),
        });
        if (r.ok) {
          const d = await r.json();
          setSaved(d.saved_at || new Date().toISOString());
          setSaveWarning("");
        } else {
          // Surface the failure so Shannon doesn't think it saved.
          setSaved(null);
          setSaveWarning(r.status === 401 ? "Sign in to save your materials."
                          : `Save failed (${r.status}) — try again.`);
        }
      } catch (e) {
        setSaved(null);
        setSaveWarning("Offline — materials will save when you reconnect.");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [rows, propertyAddress, entityId, subtotal]);

  const runCascade = async () => {
    if (!propertyAddress?.trim()) {
      toast.error("Enter a property address to run the cascade.");
      return;
    }
    if (linesFilled === 0) {
      toast.error("Add at least one material before running the cascade.");
      return;
    }
    const p = toast.loading("Running walkthrough → CapEx → Closeout cascade…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/cascade/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          property_address: propertyAddress.trim(),
          entity_id: entityId || null,
          market_rent: parseFloat(marketRent) || undefined,
        }),
      });
      const d = await r.json();
      toast.dismiss(p);
      if (!r.ok) throw new Error(d.detail || "Cascade failed");
      toast.success("Cascade complete", {
        description: `${d.stages_completed?.length || 4} stages · Stab fee $${(d.financials?.stabilization_fee || 0).toLocaleString()}`,
        duration: 8000,
      });
    } catch (e) {
      toast.dismiss(p);
      toast.error("Cascade failed", { description: String(e.message || e) });
    }
  };

  const inputStyle = null; // legacy — retained for downstream refs; input UI removed per Doctrine v3.0
  void inputStyle;

  return (
    <div data-testid="dynamic-materials-builder" style={{ paddingBottom: 120 }}>
      {/* Header */}
      <div style={{
        padding: "12px 14px", marginBottom: 14,
        background: "rgba(0,255,255,0.04)",
        border: "1px solid rgba(0,255,255,0.15)",
        borderRadius: 10,
      }}>
        <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800 }}>
          &gt; HANDS-FREE MATERIAL STREAM · AUTO-ASSEMBLED
        </div>
        <div style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 700, marginTop: 4 }}>
          {propertyAddress || <span style={{ color: "#FFB347" }}>Enter a property address above to bind this material set</span>}
        </div>
        <div style={{ color: "#7E92A8", fontSize: "0.74rem", marginTop: 4 }}>
          The digital platform assembles the procurement manifest for you — no manual entry, no locked hardware, no color-name presets. Select a variation below and the stabilization fee locks automatically. {saved && <span style={{ color: "#00DC78", marginLeft: 6 }}>✓ Saved {new Date(saved).toLocaleTimeString()}</span>}
          {saveWarning && <span style={{ color: "#FFB347", marginLeft: 6 }}>⚠ {saveWarning}</span>}
        </div>

        {/* Compliance-ready badge strip — hardwired per Doctrine v3.0 */}
        <div data-testid="dmb-compliance-badges" style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[
            { l: "48-HR TURN",            c: "#00FFFF" },
            { l: "NSPIRE COLD COALITION", c: "#00DC78" },
            { l: "CITY-COLD §8",          c: "#00DC78" },
            { l: "HUD-VASH READY",        c: "#A78BFA" },
            { l: "ACCLAMATION CLAUSE",    c: "#FFB347" },
          ].map(b => (
            <span key={b.l} style={{
              background: `${b.c}18`, border: `1px solid ${b.c}66`, color: b.c,
              fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem",
              letterSpacing: "0.14em", fontWeight: 800,
              padding: "3px 7px", borderRadius: 3,
            }}>{b.l}</span>
          ))}
        </div>

        {/* Zero-fee + 50/50 split reminder — hardwired financial doctrine */}
        <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.28)", borderRadius: 6, color: "#B4BEC8", fontSize: "0.72rem", lineHeight: 1.5 }}>
          <strong style={{ color: "#00DC78" }}>Zero platform fee · Zero broker · Zero locator · Zero management fee.</strong> Costs split 50/50 — first half is owner procurement rolled into mobilization; second half is embedded in the stabilization fee (Local Market Rent × 3 + 20% capital-recapture and quality-elevation increase). Freight and delivery to the property are the owner&apos;s procurement responsibility; field-crew mileage is tracked at IRS business rates. Current-year maintenance write-offs are cleanly separated from long-term capital expenditure allocations. Every line item below is transparent.
        </div>
      </div>

      {loading && (
        <div style={{ color: "#7E92A8", fontSize: "0.85rem", padding: "0.5rem 0" }}>Loading saved materials…</div>
      )}

      {/* ─── Property Intelligence Panel (hands-free) ─── */}
      {propIntel && propIntel.status === "intelligence_streamed" && (
        <div data-testid="dmb-property-intel" style={{
          padding: "12px 14px", marginBottom: 14,
          background: "rgba(167,139,250,0.05)",
          border: "1px solid rgba(167,139,250,0.28)",
          borderRadius: 10,
        }}>
          <div style={{ color: "#A78BFA", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800, marginBottom: 8 }}>
            &gt; PROPERTY INTELLIGENCE · BACKGROUND STREAM
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700 }}>NSPIRE CLOCK</div>
              <div style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, marginTop: 2 }}>
                {propIntel.nspire?.days_until_next_inspection}d
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.72rem" }}>
                Next: {propIntel.nspire?.next_inspection_estimate}
              </div>
            </div>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700 }}>HOUSING PROGRAMS</div>
              <div style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 700, marginTop: 2 }}>
                {propIntel.housing?.section_8_ready ? "§8 ✓" : "§8 —"} · {propIntel.housing?.hud_vash_ready ? "HUD-VASH ✓" : "HUD-VASH —"}
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.72rem" }}>City-cold ready · 48-hr turn</div>
            </div>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700 }}>BOND MATURITY</div>
              <div style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, marginTop: 2 }}>
                {propIntel.bond?.maturity_years}y
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.72rem" }}>
                Refi: {propIntel.bond?.refinance_window}
              </div>
            </div>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700 }}>PORTFOLIO</div>
              <div style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, marginTop: 2 }}>
                ~{propIntel.portfolio?.estimated_units} units
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.72rem" }}>
                {propIntel.portfolio?.portfolio_discount ? "Portfolio discount eligible" : "Single-asset pricing"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Auto-Variation Stream (hands-free · no manual entry) ─── */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800, marginBottom: 8 }}>
          &gt; AUTO-VARIATION STREAM · {walkthrough ? "LOCKED BY WALKTHROUGH" : "AWAITING WALKTHROUGH"}
          {streamPulledAt && <span style={{ color: "#7E92A8", marginLeft: 8, letterSpacing: "0.08em" }}>· pulled {new Date(streamPulledAt).toLocaleTimeString()}</span>}
        </div>
        <div style={{ color: "#7E92A8", fontSize: "0.75rem", lineHeight: 1.55, marginBottom: 10 }}>
          {walkthrough ? "The walkthrough score picked and locked the tier for this unit; the procurement manifest is built. Tap another card only to override." : "Run the field walkthrough for this address and the tier locks itself. Until then you can tap a card to preview."}
        </div>
      </div>

      {walkthrough && <WalkthroughLock w={walkthrough} />}
      {streamLoading && (
        <div style={{ color: "#7E92A8", fontSize: "0.85rem", padding: "0.75rem 0" }}>
          Streaming variations from background data feeds…
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }} data-testid="dmb-auto-variations">
        {autoVariations.map(v => {
          const isSel = v.variation_id === selectedVariationId;
          return (
            <button
              key={v.variation_id}
              type="button"
              data-testid={`dmb-variation-${v.variation_id}`}
              onClick={() => selectVariation(v)}
              style={{
                textAlign: "left",
                cursor: "pointer",
                background: isSel ? "rgba(0,220,120,0.10)" : "rgba(255,255,255,0.02)",
                border: `2px solid ${isSel ? "#00DC78" : "rgba(255,255,255,0.10)"}`,
                borderRadius: 12,
                padding: "14px 14px 12px",
                color: "#fff",
                boxShadow: isSel ? "0 8px 24px rgba(0,220,120,0.20)" : "none",
                transition: "border-color 0.15s, background 0.15s",
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <div style={{ color: isSel ? "#00DC78" : "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.72rem", letterSpacing: "0.14em", fontWeight: 800 }}>
                  {v.variation_id}
                </div>
                <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", fontWeight: 800 }}>
                  ${(v.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ fontSize: "0.95rem", fontWeight: 800, marginBottom: 4 }}>
                {v.stream_label}
              </div>
              <div style={{ color: "#B4BEC8", fontSize: "0.75rem", lineHeight: 1.45, marginBottom: 10 }}>
                {v.tone}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {CATEGORIES.map(c => {
                  const meta = v.categories?.[c.key];
                  if (!meta) return null;
                  const supplierId = meta.supplier_route?.primary || "";
                  return (
                    <div key={c.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: "0.72rem" }}>
                      <div style={{ color: "#7E92A8", minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ marginRight: 5 }}>{c.icon}</span>{meta.spec}
                        {supplierId && (
                          <span style={{ color: "#5AC8D8", marginLeft: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.08em" }}>
                            · {supplierId}
                          </span>
                        )}
                      </div>
                      <div style={{ color: "#B4BEC8", fontFamily: "'JetBrains Mono', monospace", marginLeft: 8, flex: "0 0 auto" }}>
                        {meta.quantity}× ${meta.unit_price}
                      </div>
                    </div>
                  );
                })}
              </div>

              {v.financial_split && (
                <div data-testid={`dmb-financial-split-${v.variation_id}`} style={{
                  marginTop: 10, padding: "6px 8px",
                  background: "rgba(0,220,120,0.08)",
                  border: "1px solid rgba(0,220,120,0.32)",
                  borderRadius: 4,
                  color: "#B4BEC8", fontSize: "0.62rem", lineHeight: 1.5,
                }}>
                  <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", fontWeight: 800, marginBottom: 2 }}>
                    OWNER: 100% PROCUREMENT · 50% MOBILIZATION
                  </div>
                  Second half of mobilization is embedded in stabilization fee (Market Rent × 3 + 20%). Half-of-line-total for owner draw: <strong style={{ color: "#00DC78" }}>${(v.financial_split.half_of_line_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>
                </div>
              )}
              <div style={{ color: "#FFB347", fontSize: "0.68rem", marginTop: 10, fontStyle: "italic", lineHeight: 1.4 }}>
                {v.acclamation_clause}
              </div>
              {(v.antimicrobial || v.architectural_openings) && (
                <div style={{ color: "#5AC8D8", fontSize: "0.65rem", marginTop: 6, lineHeight: 1.45 }}>
                  {v.antimicrobial && <div>🛡 {v.antimicrobial}</div>}
                  {v.architectural_openings && <div style={{ marginTop: 3 }}>🚪 {v.architectural_openings}</div>}
                </div>
              )}
              {isSel && (
                <div style={{ marginTop: 10, padding: "4px 8px", background: "rgba(0,220,120,0.15)", border: "1px solid rgba(0,220,120,0.4)", borderRadius: 4, color: "#00DC78", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.1em", textAlign: "center" }}>
                  ✓ LOCKED FOR THIS UNIT
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── Granular Micro-Material Engine (hands-free) ─── */}
      <GranularMicroMaterials unitType={unitType} measuredSpecs={walkthrough?.rooms_measured ? walkthrough.specs : null} />
      {walkthrough && <LaborAllocations w={walkthrough} />}

      {/* Legacy manual rows (kept in state, hidden from UI) power the
          existing save + cascade endpoints so every downstream feature
          (Budget tab, Cascade Engine, Resolute Closeout PDF) keeps
          working with zero refactor. Selection above fills `rows`. */}

      {/* Category rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
        {/* MANDATORY VAGRANT-KICK DEFENSE — MF-EXT-CLAMP-01. Derived
            from the selected variation's `vagrant_defense` line item
            (integrated in the auto-stream, no longer a hardcoded add-on).
            Read-only visual reminder that the row is non-optional. */}
        {(() => {
          const vd = rows.vagrant_defense || {};
          const qty = parseFloat(vd.quantity) || 0;
          const unit = parseFloat(vd.unit_cost) || 89.00;
          if (!qty && !selectedVariationId) return null;
          const lineTotal = qty * unit;
          return (
            <div data-testid="dmb-row-security" style={{
              background: "rgba(255,179,71,0.06)",
              border: "1px solid rgba(255,179,71,0.35)",
              borderRadius: 10, padding: "12px 14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 700 }}>
                  🔒 Vagrant-Kick Defense · MF-EXT-CLAMP-01
                  <span style={{ color: "#FFB347", fontWeight: 800, fontSize: "0.66rem", marginLeft: 8, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", background: "rgba(255,179,71,0.12)", padding: "2px 6px", borderRadius: 4 }}>
                    MANDATORY · READ-ONLY
                  </span>
                </div>
                <div style={{ color: "#FFB347", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.82rem", fontWeight: 800 }}>
                  {qty}× · ${lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ color: "#B4BEC8", fontSize: "0.76rem", lineHeight: 1.5 }}>
                Engineered against shoulder-kick, crowbar leverage, vagrant intrusion, and teen vandalism. Strike plate BYPASSED — heavy-duty hardware matched to each door make/model. Removable capital equipment (owner tax write-off applies). In-house maintenance uninstalls at turn completion for portfolio rotation reuse. Fire-code emergency egress preserved.
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 4, fontStyle: "italic" }}>
                Non-optional. Auto-derived from the selected variation&apos;s exterior-door count.
              </div>
            </div>
          );
        })()}
      </div>

      {/* Subtotal + cascade trigger */}
      <div className="dmb-cascade-bar" style={{
        marginTop: 14, padding: "14px 16px",
        background: "linear-gradient(135deg, rgba(0,255,255,0.08), rgba(167,139,250,0.06))",
        border: "1px solid rgba(0,255,255,0.25)",
        borderRadius: 12,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.18em" }}>
            PROCUREMENT SUBTOTAL · variation locked + mandatory security row
          </div>
          <div style={{ color: "#00FFFF", fontSize: "1.6rem", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", marginTop: 3 }}>
            ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 2 }}>
            + Stabilization fee = (Market Rent × 3) + 20%
          </div>
        </div>
        <button
          data-testid="dmb-run-cascade"
          onClick={runCascade}
          style={{
            background: "#00FFFF", color: "#030B1A",
            border: "none", borderRadius: 8,
            padding: "12px 20px", fontWeight: 800,
            fontSize: "0.85rem", letterSpacing: "0.02em",
            cursor: "pointer",
          }}
        >
          ▶ Run Walkthrough → CapEx → Closeout
        </button>
      </div>
    </div>
  );
};

export default DynamicMaterialsBuilder;
